import { cn } from "~/lib/utils";
import {
  ROOT_SESSION_STATUS_LABELS,
  type RootSessionStatus,
  rootSessionStatusFillClass,
} from "~/rootSessionStrip";

/**
 * The pip cluster — "a small multi-status-shower" (the principal, 2026-09-03):
 * one pip per child in its own hue, up to four in a 2×2 grid, so a folded
 * room or a folded plural session still tells you what is inside. Past four,
 * the count in the rail's mono voice. Hue reads the one status→color table;
 * a dormant child is the muted role, faint.
 */
const CLUSTER_CAP = 4;

export function PipCluster({
  statuses,
  noun,
  className,
}: {
  readonly statuses: ReadonlyArray<RootSessionStatus | null>;
  /** The children's noun for the announcement: "sessions", "sub-sessions". */
  readonly noun: string;
  readonly className?: string;
}) {
  const words = statuses.map((status) =>
    status === null ? "dormant" : ROOT_SESSION_STATUS_LABELS[status].toLowerCase(),
  );
  const title = `${statuses.length} ${noun} · ${words.join(" · ")}`;
  if (statuses.length > CLUSTER_CAP) {
    return (
      <span
        aria-label={title}
        className={cn(
          "flex h-3.5 min-w-3.5 shrink-0 items-center justify-center rounded-sm px-0.5 font-mono text-[8px] leading-none opacity-70",
          className,
        )}
        data-pip-cluster="count"
        title={title}
      >
        {statuses.length}
      </span>
    );
  }
  return (
    <span
      aria-label={title}
      className={cn("grid shrink-0 grid-cols-2 gap-0.5", className)}
      data-pip-cluster={String(statuses.length)}
      title={title}
    >
      {statuses.map((status, index) => (
        <i
          className={cn(
            "block size-1 rounded-full forced-colors:bg-[ButtonText]",
            status === null ? "bg-muted-foreground opacity-35" : rootSessionStatusFillClass(status),
          )}
          data-pip-status={status ?? "none"}
          key={index}
        />
      ))}
    </span>
  );
}
